import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Storable, StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {getNoteFromDocument} from 'notebook/documents/DocumentSelectors';
import DocumentStore from 'notebook/documents/DocumentStore';
import {Selector} from 'notebook/ELNConstants';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {getTableNoteParts} from 'notebook/notes/util/getTableNoteParts';
import {countSelectors} from 'notebook/plain-tables/selectors/SelectorUtil';
import excess from 'notebook/tables/excess';
import {
  TableLocationNameArgs,
  FullTableMap,
  OutputTableMap,
  PLACEHOLDER_DOC_ID,
} from 'notebook/tables/excess/Evaluator';
import {
  multidocTableOpFromOpAndShiftedFormulas,
  shiftFormulas,
  mergeShiftFormulaTableOpDiffs,
} from 'notebook/tables/excess/ops/TableOps';
import {TokenUpdater} from 'notebook/tables/excess/ops/TokenUpdater';
import * as SpreadsheetActions from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetEvalCache from 'notebook/tables/spreadsheets/SpreadsheetEvalCache';
import SpreadsheetStore, {SpreadsheetStoreState} from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {updateNoteWithTableMap} from 'notebook/tables/store/actions/TableActions';
import {getTableMapFromNote} from 'notebook/tables/store/selectors/TableSelectors';
import {
  isTextEditableType,
  MultidocShiftFormulasTableOpDiffMap,
  MultidocTableOpMap,
  MultiSelectionBox,
  ShiftFormulasTableOpDiffMap,
  Table,
  TableIndex,
  TableOpOptions,
  TypedValue,
  TableOpFunction,
  TableDimension,
  CrossTableCellColors,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {isNoteLinkedGroup as isNoteLinkedGroupType} from 'notebook/TypePredicates';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import prettyFloat from 'util/prettyFloat';

import {CellInsertMode, DEFAULT_DOCUMENT_NAME, TryInsertOptions} from '../excess/FormulaUtil';
import {NoteRuntimeSnapshot} from '../TableStoreTransforms';

export interface SpreadsheetEvalStoreState {
  elnResolution: object;
  workflows: object;
  fileSummariesById: {[id: string]: BioentitySummary};
  storablesById: {[id: string]: Storable};
  storableSummariesById: {[id: string]: StorableSummary};
}

export interface RelatedDocumentTables {
  documentId: string;
  documentName: string;
  tableMap: FullTableMap;
}

export interface FormulaShiftedTableOpOutput {
  tables: FullTableMap;
  box: MultiSelectionBox | null;
  relatedDocuments: Array<RelatedDocumentTables>;
  multidocTableOp?: MultidocTableOpMap;
}

function _didStoreDependenciesChange(
  storeState: SpreadsheetEvalStoreState,
  storeDependencies: StoreDependencyMap,
  newFileSummariesByIdReduxState: {[id: string]: BioentitySummary},
  newStorablesById: {[id: string]: Storable},
  newStorableSummariesById: {[id: string]: StorableSummary}
): boolean {
  const bioentityDependencies = storeDependencies.bioentityIds ? storeDependencies.bioentityIds : [];
  for (const bioentityId of bioentityDependencies) {
    if (newFileSummariesByIdReduxState[bioentityId] !== storeState.fileSummariesById[bioentityId]) {
      return true;
    }
  }

  const storableDependencies = storeDependencies.storableIds ? storeDependencies.storableIds : [];
  for (const storableId of storableDependencies) {
    if (newStorablesById[storableId] !== storeState.storablesById[storableId]) {
      return true;
    }
    if (newStorableSummariesById[storableId] !== storeState.storableSummariesById[storableId]) {
      return true;
    }
  }

  return false;
}

function getStorablesByIdFromStore(storableIds: Array<string>): {
  storablesById: {[id: string]: Storable};
  storableSummariesById: {[id: string]: StorableSummary};
} {
  const storablesById: {[id: string]: Storable} = {};
  const storableSummariesById: {[id: string]: StorableSummary} = {};
  for (const storableId of storableIds) {
    const storable = StorableStore.getStorable(storableId);
    const storableSummary = StorableStore.getStorableSummary(storableId);
    if (storable) {
      storablesById[storableId] = storable;
    }
    if (storableSummary) {
      storableSummariesById[storableId] = storableSummary;
    }
  }
  return {storablesById, storableSummariesById};
}

export function updateIfStoreDependenciesChanged({
  storeState,
  storeDependencies,
  tempFullTableMap,
  handleChangeTempFullTableMap,
  makeFullTableMap,
  selectorsFromProps,
  updateEvaluatedTableState,
  fileId,
  isNoteLinkedGroup,
}: {
  storeState: SpreadsheetEvalStoreState;
  storeDependencies: StoreDependencyMap;
  tempFullTableMap: FullTableMap;
  handleChangeTempFullTableMap: (newTempFullTableMap: FullTableMap) => void;
  makeFullTableMap: () => FullTableMap;
  selectorsFromProps: () => Array<Selector>;
  updateEvaluatedTableState: (
    fullTableMap: FullTableMap,
    selectors: Array<Selector>,
    noteRuntimeSnapshot: NoteRuntimeSnapshot | null
  ) => void;
  fileId: string | null;
  isNoteLinkedGroup: boolean;
}): SpreadsheetEvalStoreState {
  const newElnResolutionReduxState = getStore().getState().models.eln.resolutionResultsByResolutionQuery;
  const newWorkflowsReduxState = getStore().getState().models.workflows;
  const newFileSummariesByIdReduxState = getStore().getState().models.fileSummariesById;
  const {storablesById: newStorablesById, storableSummariesById: newStorableSummariesById} =
    getStorablesByIdFromStore(storeDependencies.storableIds || []);
  const isWorkflowReduxStateChanged = storeState.workflows !== newWorkflowsReduxState;
  let fullTableMap = tempFullTableMap;
  // TODO(BNCH-13229): Clean up legacy stage run logic
  if (isWorkflowReduxStateChanged) {
    // TODO(BNCH-5281): We need to recalculate the fullTableMap when workflows change in order to
    // update registration table default rows based on stage runs. This would not be the case if
    // default rows were persisted in registration tables.
    fullTableMap = makeFullTableMap();
    handleChangeTempFullTableMap(makeFullTableMap());
  }
  if (
    storeState.elnResolution !== newElnResolutionReduxState ||
    isWorkflowReduxStateChanged ||
    _didStoreDependenciesChange(
      storeState,
      storeDependencies,
      newFileSummariesByIdReduxState,
      newStorablesById,
      newStorableSummariesById
    )
  ) {
    // NoteLinkedGroups should trigger evaluation specifically for themselves
    if (fileId && !isNoteLinkedGroup) {
      // Let the entry store know to force an evaluation that may have updated due to store changes
      EntryActions.triggerEntryEvaluation(fileId);
    } else {
      updateEvaluatedTableState(fullTableMap, selectorsFromProps(), null);
    }
  }
  return {
    elnResolution: newElnResolutionReduxState,
    workflows: newWorkflowsReduxState,
    fileSummariesById: newFileSummariesByIdReduxState,
    storablesById: newStorablesById,
    storableSummariesById: newStorableSummariesById,
  };
}

// Evaluation without a NoteRuntimeSnapshot / Resolver
export function evaluateTables({
  fullTableMap,
  selectors,
  oldComputedTables,
  hasFormulasDisabled,
  spreadsheetEvalCacheKey,
  disallowCellRefs,
  fileId,
}: {
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
  oldComputedTables: OutputTableMap | null;
  hasFormulasDisabled: boolean;
  spreadsheetEvalCacheKey: string | null;
  disallowCellRefs?: boolean;
  fileId?: string | null;
}): {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}} {
  const prevEvalSnapshot = oldComputedTables ? {[fileId ?? PLACEHOLDER_DOC_ID]: oldComputedTables} : null;
  const evaluator = new excess.SpreadsheetEvaluator({
    fullTableMap,
    selectors,
    fileId,
    parse: hasFormulasDisabled
      ? excess.parser.parseAsValue
      : disallowCellRefs
        ? excess.parser.parseWithoutCellRefs
        : excess.parse,
    multidocTableOp: {[fileId ?? PLACEHOLDER_DOC_ID]: 'eval-document'},
    prevEvalSnapshot,
  });
  const recomputedTables = evaluator.output();
  if (spreadsheetEvalCacheKey) {
    SpreadsheetEvalCache.setValues(spreadsheetEvalCacheKey, fullTableMap, recomputedTables);
  }
  const computedTables = _.mapObject(recomputedTables, (currentTable, currentTableName) =>
    // Instead of directly using the tables of evaluator, we optimize each new table by re-using
    // subcomponents of the old table wherever possible, e.g. if we modify a single cell, typically
    // every row of a table will be unchanged. This allows the React component to avoid re-rendering
    // with PureRenderMixin.
    excess.util.cloneWithReuse({
      base: oldComputedTables != null ? oldComputedTables[currentTableName] : undefined,
      source: currentTable,
    })
  );
  return {
    computedTables,
    selectorCounts: getSelectorCounts(fullTableMap),
  };
}

// Synchronous Evaluation without a NoteRuntimeSnapshot / Resolver
export async function evaluateTablesWithResolutions({
  fullTableMap,
  selectors,
  hasFormulasDisabled,
  disallowCellRefs,
}: {
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
  hasFormulasDisabled: boolean;
  disallowCellRefs?: boolean;
}): Promise<{computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}}> {
  const evaluator = new excess.SpreadsheetEvaluator({
    fullTableMap,
    selectors,
    parse: hasFormulasDisabled
      ? excess.parser.parseAsValue
      : disallowCellRefs
        ? excess.parser.parseWithoutCellRefs
        : excess.parse,
    multidocTableOp: {[PLACEHOLDER_DOC_ID]: 'eval-document'},
  });
  const computedTables = await evaluator.outputWithStringsResolved();

  return {
    computedTables,
    selectorCounts: getSelectorCounts(fullTableMap),
  };
}

export function getSelectorCounts(fullTableMap: FullTableMap): {[selectorId: number]: number} {
  // HACK(T7258): construct a list of items that countSelectors understands.
  // eslint-disable-next-line no-restricted-syntax
  const tableItems = _.values(fullTableMap).map((table) => ({table}));
  return countSelectors(tableItems);
}

// The cell colors shouldn't change every time the spreadsheet store changes, as this will cause downstream
// re-renders that affect performance.
const EMPTY_CROSS_TABLE_CELL_COLORS: CrossTableCellColors = Object.freeze({});

export function getSpreadsheetStoreState(
  tableNamespace: string,
  hasFormulasDisabled: boolean,
  tableName?: string,
  documentName?: string
): {
  showExcessLabels: boolean;
  cellColors: CrossTableCellColors;
} & Pick<SpreadsheetStoreState, 'editingTable' | 'editingDocumentName'> {
  let showExcessLabels;
  const storeState = SpreadsheetStore.getState(tableNamespace, tableName);
  let cellColors =
    storeState.cellColors[documentName ?? DEFAULT_DOCUMENT_NAME] ?? EMPTY_CROSS_TABLE_CELL_COLORS;
  const {editingTable, editingDocumentName, formula} = storeState;
  if (hasFormulasDisabled) {
    showExcessLabels = false;
    cellColors = EMPTY_CROSS_TABLE_CELL_COLORS;
  } else {
    showExcessLabels = formula == null || excess.util.getFormulaText(formula) != null;
  }
  return {cellColors, editingTable, editingDocumentName, showExcessLabels};
}

export function setMultiEditingForTable(
  {tableName, documentName}: TableLocationNameArgs,
  tableNamespace: string,
  fileId: string | null,
  {
    index,
    editing,
    text,
    typedValue,
    selection,
  }: {
    index: TableIndex | null;
    editing: boolean;
    text: string;
    typedValue: TypedValue | null;
    selection: {cid: string; start: number; end: number} | null;
  }
): void {
  const hasTypedValue = typedValue != null && !isTypedValueEmpty(typedValue);
  SpreadsheetActions.setFormula({
    tableNamespace,
    tableName,
    documentName,
    fileId,
    index,
    formula:
      text ||
      (typedValue && isTextEditableType(typedValue) ? formatTypedValueForFormulaEditable(typedValue) : ''),
    typedValue: hasTypedValue ? typedValue : null,
    editing,
    selection,
  });
}

function formatTypedValueForFormulaEditable(typedValue: TypedValue): string {
  // Float field values can have display precision applied to their value fields, so we use rawValue for editing,
  // falling back to value (if equivalent to the floatValue), or just formatting the floatValue field for legacy
  // persisted values. Checking the value field first allows us to retain the user's input notation in legacy cases.
  if (typedValue.type === FieldType.FLOAT) {
    if (typedValue.rawValue != null) {
      return typedValue.rawValue;
    } else if (typedValue.value != null && parseFloat(typedValue.value) === typedValue.floatValue) {
      return typedValue.value;
    } else if (typedValue.floatValue != null) {
      return prettyFloat(typedValue.floatValue);
    } else {
      return '';
    }
  } else {
    return formatTypedValue(typedValue);
  }
}

export function tryInsert({
  tableName,
  documentName,
  e,
  hasFormulasDisabled,
  tableNamespace,
}: TableLocationNameArgs & {
  e: TryInsertOptions;
  hasFormulasDisabled: boolean;
  tableNamespace: string;
}): boolean {
  if (hasFormulasDisabled) {
    return false;
  }
  // We read formula directly from the store since we intentionally avoid introducing it into the state
  // as it is not necessary for rendering purposes (and would otherwise introduce perf issues).
  const store = SpreadsheetStore.getState(tableNamespace);
  const originalFormula = store.formula;
  const editingFileId = store.fileId;
  let editingIndex = store.index;
  let {editingTable, editingDocumentName} = store;
  // If there was no active edit for this namespace, then start a new edit for this table.
  if (!editingTable) {
    editingTable = tableName;
    editingIndex = e.index;
  }
  if (!editingDocumentName) {
    editingDocumentName = documentName;
  }
  const insertion = excess.FormulaUtil.tryInsert(
    {tableName, documentName},
    {editingTable, editingDocumentName},
    originalFormula,
    e
  );
  if (insertion == null) {
    return false;
  }
  const {formula, selection} = insertion;
  SpreadsheetActions.setFormula({
    tableNamespace,
    tableName: editingTable,
    documentName: editingDocumentName,
    fileId: editingFileId,
    index: editingIndex,
    formula,
    typedValue: null,
    editing: true,
    selection,
    cellInsertMode: CellInsertMode.ENABLE,
  });
  return true;
}

export function canInsert({
  e,
  hasFormulasDisabled,
  tableNamespace,
  editingTable,
  editingDocumentName,
}: {
  e: {start: number; end: number; cid: string; shift: boolean};
  hasFormulasDisabled: boolean;
  tableNamespace: string;
  editingTable: string | null;
  editingDocumentName: string | null;
}): boolean {
  if (hasFormulasDisabled) {
    return false;
  }
  const {formula} = SpreadsheetStore.getState(tableNamespace);
  return excess.FormulaUtil.canInsert({editingTable, editingDocumentName}, formula, e);
}

// Exported for testing. Not for use outside of this module.
export function performTableOpWithFormulaShifting({
  op,
  tableMap,
  computedTables,
  targetTableName,
  targetDocumentName,
  targetDocumentId,
  relatedDocuments,
}: {
  op: TableOpFunction;
  tableMap: FullTableMap;
  computedTables: OutputTableMap;
  targetTableName: string;
  targetDocumentName: string | null;
  targetDocumentId: string | null;
  relatedDocuments: Array<RelatedDocumentTables>;
}): FormulaShiftedTableOpOutput {
  const {table, tokenUpdaters, box, dim, tableOp} = op(
    tableMap[targetTableName],
    targetDocumentName,
    computedTables[targetTableName],
    tableMap
  );

  // Create new table map with the modified table
  const newTables: FullTableMap = {...tableMap, [targetTableName]: table};

  const formulaShiftedTables = performFormulaShifting({
    tableMap: newTables,
    tokenUpdaters,
    documentName: targetDocumentName,
    documentId: targetDocumentId,
    dim,
    relatedDocuments,
  });

  let multidocTableOp: MultidocTableOpMap | undefined;
  if (tableOp != null && targetDocumentId != null) {
    multidocTableOp = multidocTableOpFromOpAndShiftedFormulas(
      targetDocumentId,
      tableOp,
      formulaShiftedTables.multidocTableOp
    );
  } else {
    multidocTableOp = formulaShiftedTables.multidocTableOp;
  }

  return {
    tables: formulaShiftedTables.tables,
    relatedDocuments: formulaShiftedTables.relatedDocuments,
    box,
    multidocTableOp,
  };
}

export function performFormulaShifting({
  tableMap,
  tokenUpdaters,
  documentName: targetDocumentName,
  documentId,
  dim,
  relatedDocuments,
}: {
  tableMap: FullTableMap;
  tokenUpdaters: Array<TokenUpdater>;
  documentName: string | null;
  documentId: string | null;
  dim: TableDimension | null;
  relatedDocuments: Array<RelatedDocumentTables>;
}): {
  tables: FullTableMap;
  relatedDocuments: Array<RelatedDocumentTables>;
  multidocTableOp?: MultidocShiftFormulasTableOpDiffMap;
} {
  let newTables = tableMap;
  let newTableOp: ShiftFormulasTableOpDiffMap = {};
  // Formula shift tables in the target doc (local references only)
  for (const tokenUpdater of tokenUpdaters) {
    const {tables, tableOp} = shiftFormulas(newTables, tokenUpdater, targetDocumentName, dim ?? undefined);
    newTables = tables;
    newTableOp = mergeShiftFormulaTableOpDiffs(newTableOp, tableOp);
  }
  let multidocTableOp: MultidocShiftFormulasTableOpDiffMap | undefined = {};
  if (documentId != null && Object.keys(newTableOp).length > 0) {
    multidocTableOp[documentId] = newTableOp;
  }

  const updatedRelatedDocuments: Array<RelatedDocumentTables> = [];
  // Only process cross-document references if we have a valid target document name
  if (
    targetDocumentName &&
    documentId &&
    !_.isEmpty(relatedDocuments) &&
    BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
  ) {
    // Formula shift tables in all other docs (cross-document references only)
    relatedDocuments.forEach((relatedDocument) => {
      if (relatedDocument.documentId !== documentId) {
        // Apply cross-document formula shifting
        const originalDocTableMap = relatedDocument.tableMap;
        let updatedDocTableMap = originalDocTableMap;
        newTableOp = {};
        for (const tokenUpdater of tokenUpdaters) {
          const {tables, tableOp} = shiftFormulas(
            updatedDocTableMap,
            tokenUpdater,
            relatedDocument.documentName,
            dim ?? undefined
          );
          updatedDocTableMap = tables;
          newTableOp = mergeShiftFormulaTableOpDiffs(newTableOp, tableOp);
        }
        if (Object.keys(newTableOp).length > 0) {
          multidocTableOp![relatedDocument.documentId] = newTableOp;
        }
        // Only include documents that actually changed
        const hasAnyTableChange = Object.keys(updatedDocTableMap).some(
          (tableName) => updatedDocTableMap[tableName] !== originalDocTableMap[tableName]
        );
        if (hasAnyTableChange) {
          updatedRelatedDocuments.push({
            documentId: relatedDocument.documentId,
            documentName: relatedDocument.documentName,
            tableMap: updatedDocTableMap,
          });
        }
      }
    });
  }

  // If no formulas were shifted, don't return a table op
  if (Object.keys(multidocTableOp).length === 0) {
    multidocTableOp = undefined;
  }

  return {
    tables: newTables,
    relatedDocuments: updatedRelatedDocuments,
    multidocTableOp,
  };
}

/**
 * Performs an operation on a table, updating the appropriate react components
 * @param  {String} tableName The name of the target table to operate on
 * @param  {Function} op    (originalTableMap, computedTables, targetTableName) ->
 *                          {tables: newTableMap, box}, where
 *                            originalTableMap {Object}  Maps original names of tables to original tables.
 *                            targetTableName {String}   The name of the table being directly modified
 *                            newTableMap {Object}       Maps *original* names of tables to new tables.
 *                            box {Object}               {minI, maxI, minJ, maxJ}, or null if no selection
 *                                                       is specified.
 */
export function performTableOp<TRoot, TItem>({
  tableName: targetTableName,
  fileId,
  op,
  options,
  spreadsheetTypes,
  computedTables,
  getItems,
  getRoot,
  getType,
  getCid,
  getContent,
  setContent,
  requestSelection,
  updateRoot,
  itemsAccessor,
}: {
  tableName: string;
  fileId: string | null;
  op: TableOpFunction;
  options?: TableOpOptions;
  spreadsheetTypes: Array<string>;
  computedTables: OutputTableMap;
  getItems: (root: TRoot) => Array<TItem>;
  getRoot: () => TRoot;
  getType: (item: TItem) => string;
  getCid: (item: TItem) => string;
  getContent: (item: TItem) => {table: Table};
  setContent: (type: string, content: object) => Spec<TItem>;
  requestSelection: (selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex>) => void;
  updateRoot: (
    root: TRoot,
    opts?: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
      multidocTableOp?: MultidocTableOpMap;
    },
    updatedRelatedDocuments?: {[documentId: string]: Note}
  ) => void;
  itemsAccessor: (op: {[i: number]: Spec<TItem>}) => Spec<TRoot>;
}): void {
  const items = getItems(getRoot());

  // Build oldTables from ALL table items, including those nested inside NLG groups.
  // For regular spreadsheet items, use the caller's getType/spreadsheetTypes abstraction.
  // For non-spreadsheet items (e.g. NLG groups), use getTableNoteParts to traverse into them.
  const oldTables: FullTableMap = {};
  let targetItem: TItem | undefined;
  for (const item of items) {
    if (spreadsheetTypes.includes(getType(item))) {
      const {table} = getContent(item);
      oldTables[table.name] = table;
      if (table.name === targetTableName) {
        targetItem = item;
      }
    } else {
      for (const part of getTableNoteParts(item as NotePart)) {
        if (!spreadsheetTypes.includes(getType(part as TItem))) {
          continue;
        }
        const {table} = getContent(part as TItem);
        oldTables[table.name] = table;
        if (table.name === targetTableName) {
          targetItem = part as TItem;
        }
      }
    }
  }

  assert(targetItem != null, `performTableOp: target table "${targetTableName}" not found in items`);
  const resolvedTargetItem: TItem = targetItem;

  // Get the document name from the fileId using DocumentStore
  const isDocument = fileId && DocumentStore.inStore(fileId);
  const document = isDocument ? DocumentStore.getDocument(fileId) : null;
  const targetDocumentName = document?.name ?? null;

  const relatedDocumentsIds =
    isDocument && BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
      ? DocumentStore.getRelatedDocumentIds(fileId)
      : [];

  const relatedDocuments = relatedDocumentsIds
    .map((documentId) => {
      const document = DocumentStore.getDocument(documentId);
      if (!document) {
        return null;
      }
      return {
        documentId,
        documentName: document.name,
        tableMap: getTableMapFromNote(getNoteFromDocument(document)),
      };
    })
    .filter((document) => document != null);

  const {
    tables: newTables,
    box,
    relatedDocuments: updatedRelatedDocuments,
    multidocTableOp,
  } = _performTableOpWithGuards({
    tableMap: oldTables,
    computedTables,
    targetTableName,
    targetDocumentName,
    targetDocumentId: fileId,
    relatedDocuments,
    op,
    options,
  });

  const updatedRelatedDocumentsNotes: {[documentId: string]: Note} = {};
  // Perform formula shifting for related documents
  if (
    targetDocumentName &&
    fileId &&
    updatedRelatedDocuments &&
    !_.isEmpty(updatedRelatedDocuments) &&
    BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
  ) {
    updatedRelatedDocuments.forEach((relatedDocument) => {
      if (relatedDocument.documentId !== fileId) {
        const updatedNote = updateNoteWithTableMap(
          relatedDocument.tableMap,
          getNoteFromDocument(DocumentStore.getDocument(relatedDocument.documentId)!)
        );
        updatedRelatedDocumentsNotes[relatedDocument.documentId] = updatedNote;
      }
    });
  }

  // For each table that has changed, register an update for the corresponding note part.
  // This handles both flat table items and tables nested inside NLG groups.
  const itemUpdates: {[i: number]: Spec<TItem>} = {};
  // we need to loop over the actual tree of items to find the correct indexes to update, so we can't just loop
  // over the flat table list from getAllTableNoteParts
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (spreadsheetTypes.includes(getType(item))) {
      // Regular table note part
      const {table} = getContent(item);
      const newTable = newTables[table.name];
      if (newTable === table) {
        continue;
      }
      if (TableUtil.isInvalidOpWithWarn(table, newTable, options)) {
        return;
      }
      itemUpdates[i] = setContent(getType(item), {table: newTable});
    } else if ((item as NotePart).item && isNoteLinkedGroupType((item as NotePart).item!)) {
      // Check if this is an NLG group containing table note parts
      const innerTableParts = getTableNoteParts(item as NotePart);
      if (innerTableParts.length > 0) {
        const group = (item as NotePart).item as UnknownNoteLinkedGroup;
        const nestedUpdates: {[j: number]: Spec<TItem>} = {};
        for (let j = 0; j < group.containedNoteParts.length; j++) {
          const containedPart = group.containedNoteParts[j];
          if (spreadsheetTypes.includes(getType(containedPart as TItem))) {
            const {table} = getContent(containedPart as TItem);
            const newTable = newTables[table.name];
            assertAndContinue(
              newTable != null,
              `performTableOp: expected table "${table.name}" in newTables for NLG group`
            );
            if (newTable == null || newTable === table) {
              continue;
            }
            if (TableUtil.isInvalidOpWithWarn(table, newTable, options)) {
              return;
            }
            nestedUpdates[j] = setContent(getType(containedPart as TItem), {table: newTable});
          }
        }
        if (Object.keys(nestedUpdates).length > 0) {
          itemUpdates[i] = setContent(getType(items[i]), {item: {containedNoteParts: nestedUpdates}});
        }
      }
    }
  }

  // Update note parts and set selection
  // The multi selection must be updated (`@props.requestSelection`) before
  // calling `@updateRoot` to prevent the table updating with the currently
  // stale multi selection.
  if (box != null) {
    const selection: MetadataForRestore<TableIndex> = {
      multiSelectKey: TableUtil.getTableMultiSelectKey(getCid(resolvedTargetItem)),
      data: {
        multiActive: false,
        multiSelIndex: [box.minI, box.minJ],
        multiStartIndex: [box.minI, box.minJ],
        multiEndIndex: [box.maxI, box.maxJ],
        multiActualIndex: [box.minI, box.minJ],
      },
    };
    requestSelection(selection);
  }
  updateRoot(
    reactUpdate<TRoot>(getRoot(), itemsAccessor(itemUpdates)),
    {multidocTableOp},
    !_.isEmpty(updatedRelatedDocumentsNotes) ? updatedRelatedDocumentsNotes : undefined
  );
}

interface PerformTableOpsWithGuardsParams {
  tableMap: FullTableMap;
  computedTables: OutputTableMap;
  targetTableName: string;
  targetDocumentName: string | null;
  targetDocumentId: string | null;
  relatedDocuments: Array<RelatedDocumentTables>;
  op: TableOpFunction;
  options?: TableOpOptions;
}

/** @package Exported only for testing, use performTableOp instead.
 *
 * Performs a given table operation with any additional surrounding actions needed to perform the operation
 * correctly. For example, we sometimes need to unmerge single input columns before performing an operation.
 */
export function _performTableOpWithGuards({
  tableMap: oldTables,
  computedTables,
  targetTableName,
  targetDocumentName,
  targetDocumentId,
  relatedDocuments,
  op,
  options,
}: PerformTableOpsWithGuardsParams): FormulaShiftedTableOpOutput {
  // If not otherwise specified, before applying the main table operation, unmerge all single inputs in the
  // target table. We still keep them marked as single inputs because some operations (like delete row) behave
  // differently on single inputs.
  const remakeSingleInputs = Boolean(options?.remakeSingleInputs);
  const originalSingleInputBoundaryIndex = remakeSingleInputs
    ? TableUtil.findSingleInputBoundaryIndex(oldTables[targetTableName])
    : 0;

  let updatedTables: FullTableMap = oldTables;
  let updatedRelatedDocuments: Array<RelatedDocumentTables> = relatedDocuments;

  if (remakeSingleInputs && originalSingleInputBoundaryIndex) {
    const targetTable = oldTables[targetTableName];
    const numRows = targetTable.table.length;
    const selection: MultiSelectionBox = {
      minI: 0,
      maxI: numRows - 1,
      minJ: 0,
      maxJ: originalSingleInputBoundaryIndex - 1,
    };
    const unmergeOutput = performTableOpWithFormulaShifting({
      op: (table, documentName, evaluatedTable) => excess.ops.unmergeRegion(table, evaluatedTable, selection),
      tableMap: oldTables,
      computedTables,
      targetTableName,
      targetDocumentName,
      targetDocumentId,
      relatedDocuments,
    });
    updatedTables = unmergeOutput.tables;
    // Note: We intentionally do NOT update updatedRelatedDocuments here.
    // The mergeIntoSingleInput operation only affects the local table (merging cells)
  }

  // Perform the main table operation
  const tableOpResult = performTableOpWithFormulaShifting({
    op,
    tableMap: updatedTables,
    computedTables,
    targetTableName,
    targetDocumentName,
    targetDocumentId,
    relatedDocuments,
  });
  updatedTables = tableOpResult.tables;
  updatedRelatedDocuments = tableOpResult.relatedDocuments;
  // Remerge all single input columns afterward if we unmerged them before the operation.
  // Also adjust the selection box to exclude single input columns.
  let adjustedBox = tableOpResult.box;
  if (remakeSingleInputs && originalSingleInputBoundaryIndex) {
    const newSingleInputBoundaryIndex = TableUtil.findSingleInputBoundaryIndex(
      updatedTables[targetTableName]
    );
    const mergeOutput = performTableOpWithFormulaShifting({
      op: (table, documentName, evaluatedTable) =>
        excess.ops.mergeIntoSingleInput(
          table,
          documentName,
          evaluatedTable,
          0,
          newSingleInputBoundaryIndex - 1
        ),
      tableMap: updatedTables,
      computedTables,
      targetTableName,
      targetDocumentName,
      targetDocumentId,
      relatedDocuments,
    });
    updatedTables = mergeOutput.tables;
    // Note: We intentionally do NOT update updatedRelatedDocuments here.
    // The mergeIntoSingleInput operation only affects the local table (merging cells)

    // Adjust the selection box's minJ value to exclude single input columns if needed.
    // This still allows single inputs to be selected after the operation in cases where
    // we are adding or removing single input fields.
    if (
      adjustedBox != null &&
      newSingleInputBoundaryIndex > 0 &&
      adjustedBox.minJ < newSingleInputBoundaryIndex &&
      adjustedBox.maxJ > adjustedBox.minJ
    ) {
      adjustedBox = {
        ...tableOpResult.box!,
        minJ: newSingleInputBoundaryIndex,
      };
    }
  }

  return {
    tables: updatedTables,
    box: adjustedBox,
    relatedDocuments: updatedRelatedDocuments,
    multidocTableOp: tableOpResult.multidocTableOp,
  };
}
